import {
  IOnlineBookingRequestPayload,
  IOnlineBookingService,
} from "services/online-booking/OnlineBookingService.types";
import { Cradle } from "services/serviceContainer.types";
import { IOnlineBookingEntity, OnlineBookingStatusUtil } from "models/OnlineBooking.model";
import { ServiceError, ServiceErrorCode } from "services/ServiceError";
import get from "lodash/get";
import toString from "lodash/toString";
import {
  IOnlineBookingInspectionDataEntity,
  IOnlineBookingInspectionType,
} from "models/OnlineBookingInspectionDataModel";
import toInteger from "lodash/toInteger";

export class OnlineBookingService implements IOnlineBookingService {
  private readonly apiClient: Cradle["apiClient"];

  constructor(args: Pick<Cradle, "apiClient">) {
    this.apiClient = args.apiClient;
  }

  async requestBooking(args: {
    payload: IOnlineBookingRequestPayload;
    applicationId: number;
  }): Promise<IOnlineBookingEntity> {
    try {
      const response = await this.apiClient.protectedApi.post(
        `/user/applications/${args.applicationId}/inspection-bookings`,
        {
          ...args.payload,
        }
      );
      const json = response.data;
      const onlineBooking = this.parseOnlineBooking(json, args.applicationId);
      return onlineBooking;
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  async fetchOnlineBookings(args: { applicationId: number }): Promise<IOnlineBookingEntity[]> {
    try {
      const response = await this.apiClient.protectedApi.get(
        `/user/applications/${args.applicationId}/inspection-bookings`
      );
      const jsonArr = get(response.data, "bookings");
      if (!Array.isArray(jsonArr)) {
        throw new ServiceError(ServiceErrorCode.ServerError, `Failed to fetch online bookings`);
      }
      return this.parseOnlineBookings(jsonArr, args.applicationId);
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  async fetchAvailableBookingTimes(args: { applicationId: number; inspectionType: string }): Promise<string[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("inspection_type", args.inspectionType);
      const response = await this.apiClient.protectedApi.get(
        `/user/applications/${args.applicationId}/inspection-bookings/available-timeslots?${queryParams.toString()}`
      );
      const jsonArr = get(response.data, "availableTimes");
      if (!Array.isArray(jsonArr)) {
        throw new ServiceError(ServiceErrorCode.ServerError, `Failed to fetch online booking available times`);
      }
      return jsonArr;
    } catch (e) {
      throw ServiceError.createFromResponseError(e);
    }
  }

  async fetchInspectionData(args: { applicationId: number }) {
    const response = await this.apiClient.protectedApi.get(
      `/user/applications/${args.applicationId}/inspection-bookings/inspection-data`
    );
    const jsonArr = get(response.data, "buildings");
    if (!Array.isArray(jsonArr)) {
      throw new ServiceError(ServiceErrorCode.ServerError, `Failed to fetch online booking available times`);
    }
    return this.parseInspectionDatas(jsonArr, args.applicationId);
  }

  private parseInspectionDatas(jsonArr: any[], applicationId: number) {
    const inspectionData: IOnlineBookingInspectionDataEntity[] = [];
    for (const json of jsonArr) {
      inspectionData.push(this.parseInspectionData(json, applicationId));
    }
    return inspectionData;
  }

  private parseInspectionData(json: any, applicationId: number): IOnlineBookingInspectionDataEntity {
    const inspectionTypeArray = get(json, "inspectionTypes");
    const inspectionTypes: IOnlineBookingInspectionType[] = [];
    for (const json of inspectionTypeArray) {
      inspectionTypes.push(this.parseInspectionType(json));
    }
    return {
      id: toInteger(get(json, "id")),
      applicationId,
      location: toString(get(json, "name")),
      inspectionTypes,
    };
  }

  private parseInspectionType(json: string): IOnlineBookingInspectionType {
    return {
      id: toInteger(get(json, "id")),
      name: toString(get(json, "name")),
      onlineBookingEnabled: Boolean(get(json, "onlineBookingEnabled")),
      morningBookingEnabled: Boolean(get(json, "morningBookingEnabled")),
      afternoonBookingEnabled: Boolean(get(json, "afternoonBookingEnabled")),
      hasExistingBooking: Boolean(get(json, "hasExistingBooking")),
    };
  }

  private parseOnlineBookings(jsonArr: any[], applicationId: number) {
    const bookings: IOnlineBookingEntity[] = [];
    for (const json of jsonArr) {
      bookings.push(this.parseOnlineBooking(json, applicationId));
    }
    return bookings;
  }

  private parseOnlineBooking(json: string, applicationId: number): IOnlineBookingEntity {
    return {
      applicationId,
      id: toString(get(json, "bookingId")),
      inspectionType: toString(get(json, "inspectionType")),
      location: toString(get(json, "location")),
      siteContactName: toString(get(json, "siteContactName")),
      notes: toString(get(json, "notes")),
      bookingTime: toString(get(json, "bookingTime")),
      status: OnlineBookingStatusUtil.parse(toString(get(json, "status"))),
    };
  }
}
